import { pickBy } from "lodash";
import { GITBOOK_NAMESPACE, NiraAuth0UserType } from "./auth0UserType";
import { isKnownRegionType, RegionType } from "./regionType";
import { convertToRoleType, RoleType } from "./roleType";
import {
    convertHasuraScopesToNiraScopes,
    HasuraScope,
    isLocalStudy,
    Scope
} from "./scope";
import {
    HasuraSubscription,
    hasuraSubscriptionsToNiraSubscriptions,
    Subscription
} from "./subscriptionType";
import {
    convertToTeamFeatureFlagTypeList,
    TeamFeatureFlagType
} from "./teamFeatureFlagType";
import {
    convertToUserFeatureFlagTypeList,
    UserFeatureFlagType
} from "./userFeatureFlagType";

export type UserId = number;
export type TeamId = number;
export type Auth0Id = string;

export type User = {
    readonly id: UserId;
    readonly auth0Id: Auth0Id;
    readonly teamId: TeamId;
    readonly teamName: string;
    readonly teamFeatureFlags: ReadonlyArray<TeamFeatureFlagType>;
    readonly userFeatureFlags: ReadonlyArray<UserFeatureFlagType>;
    readonly isColorblindMode: boolean;
    readonly subscriptions: ReadonlyArray<Subscription>;
    readonly scopes: ReadonlyArray<Scope>;
    readonly gitbookTokens?: {
        readonly inQueueCommonDocsGitbookToken: string;
        readonly commonDocsGitbookToken: string;
        readonly regionGitbookTokens: { [K in RegionType]?: string };
    };
    readonly role: RoleType;
};

type HasuraTeam = {
    readonly name: string;
    readonly feature_flags: unknown;
    readonly subscriptions: HasuraSubscription[];
};

export type HasuraUser = {
    readonly id: number;
    readonly auth0_id: string;
    readonly team_id: number;
    readonly team: HasuraTeam;
    readonly role: string;
    readonly feature_flags: unknown;
    readonly colorblind_mode: boolean;
};

export const hasuraUserToNiraUser = (
    hasuraUser: HasuraUser,
    auth0User: NiraAuth0UserType,
    hasuraScopes: HasuraScope[]
): User => {
    const subscriptions = hasuraSubscriptionsToNiraSubscriptions(
        hasuraUser.team.subscriptions
    );

    const maybeGitbookTokens = auth0User[GITBOOK_NAMESPACE];

    const userFeatureFlags = convertToUserFeatureFlagTypeList(
        hasuraUser.feature_flags
    );

    return {
        id: hasuraUser.id,
        auth0Id: hasuraUser.auth0_id,
        teamId: hasuraUser.team_id,
        teamName: hasuraUser.team.name,
        teamFeatureFlags: convertToTeamFeatureFlagTypeList(
            hasuraUser.team.feature_flags
        ),
        userFeatureFlags: userFeatureFlags,
        isColorblindMode: hasuraUser.colorblind_mode,
        subscriptions: subscriptions,
        // Pipeline scopes only. Local studies are per-bus and belong to the
        // bus page; this list feeds the region-wide map scope selector.
        scopes: convertHasuraScopesToNiraScopes(hasuraScopes).filter(
            (scope) => !isLocalStudy(scope)
        ),
        gitbookTokens: maybeGitbookTokens && {
            commonDocsGitbookToken: maybeGitbookTokens.commonDocs,
            inQueueCommonDocsGitbookToken: maybeGitbookTokens.commonInQueueDocs,
            regionGitbookTokens: convertRegionGitbookTokens(
                maybeGitbookTokens.regionDocs
            )
        },
        role: convertToRoleType(hasuraUser.role)
    };
};

const convertRegionGitbookTokens = (rawGitbookTokens: {
    [K in string]: string;
}): { [K in RegionType]?: string } => {
    return pickBy(rawGitbookTokens, (_, maybeRegion) =>
        isKnownRegionType(maybeRegion)
    );
};
